/**
 * GraphQL implementation of the programme platform client contract.
 *
 * Speaks to the API's single `POST <baseUrl>/graphql` endpoint. Every contract
 * method executes one operation whose root field is named after it, so the
 * mapping between this adapter and the server's resolvers is mechanical.
 *
 * The host application supplies an access-token provider. This adapter owns
 * transport concerns only; tenant, identity, authorization, workflow, and
 * audit decisions remain backend responsibilities.
 *
 * Failures arrive the way GraphQL specifies, as an `errors` entry on a 200,
 * with the platform's mapped status, `code` and `details` in its `extensions`.
 * They are raised as {@link PlatformApiError}, so nothing upstream had to change
 * when the transport did.
 *
 * @module platform/graphql-client
 */

import type { ApolloClient } from "@apollo/client";

import type { ProgrammePublicationPreview } from "@/publication/types";
import type { ApplicableRevision } from "@/server/governance-applicability";
import type { ChangeCaseRevisionComparison, RevisionComparison } from "@/server/governance-diff";
import type {
  AuditEvent,
  CurriculumChangeCase,
  CurriculumPublication,
  EditLease,
  ExportRecord,
  FinalSignOff,
  GovernanceFinding,
  ModuleOwnershipAssignment,
  NotificationListQuery,
  Programme,
  ProgrammeResponsibilityAssignment,
  ProgrammeReviewOutcome,
  RevisionApplicability,
  Tenant,
  TenantAccessPolicy,
  TenantEntitlementDecision,
  TenantProviderSettings,
} from "@/server/types";

import { PlatformApiError } from "./api-error";
import type {
  AcquireEditLeaseInput,
  AppendNarrativeUpdatesInput,
  ApproveChangeCaseInput,
  ApproveChangeCaseResult,
  ApproveProgrammeInput,
  ApproveProgrammeResult,
  AssignedModuleSummary,
  AssignModuleOwnerInput,
  AssignProgrammeResponsibilityInput,
  AuditCountQuery,
  AuditQuery,
  ChangeCaseWorkspace,
  ChangeTenantUserCapabilitiesInput,
  ChangeTenantUserEmailInput,
  ChangeTenantUserRoleInput,
  ChangeTenantUserStatusInput,
  CompareGovernanceRevisionsInput,
  CompleteReviewActionInput,
  CompleteReviewActionResult,
  ConfirmChangeCaseTeamInput,
  ConfirmChangeCaseTeamResult,
  CreateGovernanceFindingInput,
  CreateModuleInput,
  CreateProgrammeInput,
  CreateReplacementProgrammeInput,
  CreateReplacementProgrammeResult,
  CreateRevisionApplicabilityInput,
  CreateTenantInput,
  CurrentSession,
  DecideChangeCaseIdentityImpactInput,
  DiscardChangeCaseInput,
  FinalExportInput,
  FinalExportResult,
  InviteTenantUserInput,
  MarkChangeCaseReviewedInput,
  ModuleWorkspaceRecord,
  NarrativeUpdate,
  NegotiateRealtimeConnectionInput,
  NotificationFeed,
  OpenAmendmentInput,
  OpenAmendmentResult,
  OpenRevalidationInput,
  OpenRevalidationResult,
  PlatformAdminOverview,
  PortfolioProgrammeRevision,
  PortfolioSummary,
  PreviewProgrammePublicationInput,
  ProgrammeGovernanceSummary,
  ProgrammePlatformClient,
  ProgrammeReviewRoundTimeline,
  ProgrammeSummary,
  PublicCurriculumApiAccess,
  PublishProgrammeInput,
  RealtimeConnection,
  RecordExportPreviewInput,
  RecordFinalSignOffInput,
  ReissueTenantInvitationInput,
  ReleaseEditLeaseInput,
  RenewEditLeaseInput,
  ResendTenantInvitationInput,
  ResolveGovernanceFindingInput,
  ResolveRevisionApplicabilityInput,
  ReviewChangeCaseInput,
  ReviewChangeCaseResult,
  ReviewModuleInput,
  ReviewOutcomeInput,
  ReviseFromSnapshotInput,
  RevokeModuleOwnerInput,
  RevokeProgrammeResponsibilityInput,
  SaveChangeCaseWorkspaceInput,
  SaveModuleWorkspaceInput,
  SaveProgrammeInput,
  SessionUser,
  SetProgrammeDeliveryStatusInput,
  SetProgrammeEndOfLifeInput,
  SetTenantPublicCurriculumApiEntitlementInput,
  SubmitChangeCaseForReviewInput,
  SubmitChangeCaseForReviewResult,
  SubmitModuleForReviewInput,
  TenantOnboardingSummary,
  UpdateMyPreferencesInput,
  UserSummary,
  WithdrawChangeCaseInput,
  WithdrawProgrammePublicationInput,
  WorkflowTransitionInput,
  WorkflowTransitionResult,
} from "./client";
import { decodePayload, encodeEnumName, payloadTypeOf } from "./graphql-enums";
import {
  AMENDMENT_PROPOSAL_FIELDS,
  APPROVAL_CANDIDATE_FIELDS,
  APPROVAL_DECISION_FIELDS,
  ASSIGNED_MODULE_FIELDS,
  AUDIT_EVENT_FIELDS,
  AUTHORIZATION_SUBJECT_FIELDS,
  CAS_DEVIATION_NOTE_FIELDS,
  CHANGE_CASE_READINESS_FIELDS,
  CHANGE_CASE_REVISION_COMPARISON_FIELDS,
  CROSS_MODULE_ASSESSMENT_PARTICIPATION_FIELDS,
  CURRICULUM_CHANGE_CASE_FIELDS,
  CURRICULUM_PUBLICATION_FIELDS,
  CURRICULUM_REVISION_FIELDS,
  DELIVERY_STATUS_COUNT_FIELDS,
  EDIT_LEASE_FIELDS,
  EXPORT_FIELDS,
  EXPORT_METADATA_ENTRY_FIELDS,
  FINAL_SIGN_OFF_FIELDS,
  GOVERNANCE_FINDING_FIELDS,
  GOVERNANCE_REVISION_FIELDS,
  IDENTITY_IMPACT_DECISION_FIELDS,
  INCOMING_CROSS_MODULE_ASSESSMENT_FIELDS,
  MARK_ALL_NOTIFICATIONS_READ_PAYLOAD_FIELDS,
  MARK_NOTIFICATION_READ_PAYLOAD_FIELDS,
  MIMLO_FIELDS,
  MODULE_ASSESSMENT_FIELDS,
  MODULE_FIELDS,
  MODULE_OWNER_FIELDS,
  MODULE_REVIEW_FIELDS,
  MODULE_SUBMISSION_FIELDS,
  MODULE_SUBMISSION_WARNING_FIELDS,
  MODULE_WORKSPACE_FIELDS,
  MODULE_WORKSPACE_VERSION_CONTEXT_FIELDS,
  NARRATIVE_UPDATE_FIELDS,
  NOTIFICATION_FEED_FIELDS,
  NOTIFICATION_FEED_ITEM_FIELDS,
  PLATFORM_ADMIN_OVERVIEW_FIELDS,
  PLATFORM_ADMINISTRATIVE_AUDIT_EVENT_FIELDS,
  PLATFORM_TENANT_SUMMARY_FIELDS,
  PLO_FIELDS,
  PLO_STANDARD_MAPPING_FIELDS,
  PLO_TRACEABILITY_FIELDS,
  PORTFOLIO_PROGRAMME_REVISION_FIELDS,
  PORTFOLIO_PUBLICATION_SUMMARY_FIELDS,
  PORTFOLIO_SUMMARY_FIELDS,
  PROGRAMME_GOVERNANCE_SUMMARY_FIELDS,
  PROGRAMME_IDENTITY_FIELD_CHANGE_FIELDS,
  PROGRAMME_PUBLICATION_PREVIEW_FIELDS,
  PROGRAMME_RELATIONSHIP_FIELDS,
  PROGRAMME_RESPONSIBILITY_FIELDS,
  PROGRAMME_REVIEW_OUTCOME_FIELDS,
  PROGRAMME_REVIEW_ROUND_REVIEWER_STATUS_FIELDS,
  PROGRAMME_REVIEW_ROUND_TIMELINE_FIELDS,
  PROVIDER_CONTACT_FIELDS,
  PROVIDER_SETTINGS_FIELDS,
  PUBLIC_CURRICULUM_API_ACCESS_FIELDS,
  PUBLICATION_STATUS_FIELDS,
  PUBLICATION_VALIDATION_ISSUE_FIELDS,
  READINESS_BLOCKER_FIELDS,
  READINESS_BLOCKER_SNAPSHOT_FIELDS,
  READINESS_BLOCKER_TARGET_FIELDS,
  READINESS_OVERRIDE_DECISION_FIELDS,
  READINESS_RESULT_FIELDS,
  READING_LIST_ITEM_FIELDS,
  REALTIME_CONNECTION_FIELDS,
  RELEASE_EDIT_LEASE_PAYLOAD_FIELDS,
  REVIEW_ROUND_FIELDS,
  REVISION_APPLICABILITY_FIELDS,
  REVISION_COMPARISON_FIELDS,
  REVISION_FIELD_CHANGE_FIELDS,
  REVISION_IMPACT_REPORT_FIELDS,
  REVISION_LIFECYCLE_ENTRY_FIELDS,
  REVISION_PUBLICATION_ENTRY_FIELDS,
  REVOKE_MODULE_OWNER_PAYLOAD_FIELDS,
  REVOKE_PROGRAMME_RESPONSIBILITY_PAYLOAD_FIELDS,
  SESSION_CONTEXT_FIELDS,
  SESSION_USER_FIELDS,
  SNAPSHOT_FIELDS,
  TENANT_ACCESS_POLICY_FIELDS,
  TENANT_CONFIGURATION_GAP_FIELDS,
  TENANT_CONTEXT_FIELDS,
  TENANT_ENTITLEMENT_DECISION_FIELDS,
  TENANT_FIELDS,
  TENANT_MEMBERSHIP_FIELDS,
  TENANT_ONBOARDING_SUMMARY_FIELDS,
  TENANT_PROVIDER_SETTINGS_FIELDS,
  USER_FIELDS,
  WORKFLOW_STATE_COUNT_FIELDS,
} from "./graphql-fragments.generated";
import { createGraphqlTransport, runOperation } from "./graphql-transport";
import type {
  DeliveryStatusCount,
  ExportMetadataEntry,
  MarkAllNotificationsReadPayload,
  MarkNotificationReadPayload,
  PloTraceability,
  ReleaseEditLeasePayload,
  RevokeModuleOwnerPayload,
  RevokeProgrammeResponsibilityPayload,
  WorkflowStateCount,
} from "./platform-contract.generated";
import { PROGRAMME_RECORD_SELECTION, PROGRAMME_SUMMARY_SELECTION } from "./programme-selections";

export { PlatformApiError, PROGRAMME_RECORD_SELECTION, PROGRAMME_SUMMARY_SELECTION };

export type PlatformAccessTokenProvider = () => Promise<string | null | undefined>;

export interface GraphqlProgrammePlatformClientOptions {
  /** API root, for example `https://api.example.edu/api/v1`. */
  baseUrl: string;
  /** Supplies a current Entra access token immediately before each request. */
  getAccessToken: PlatformAccessTokenProvider;
  /**
   * The tenant this client acts in, sent as the tenant-selection header on every
   * request. A user with several tenant memberships needs this to say which one
   * they are acting in; the server still verifies the caller may reach it.
   */
  tenantId?: string;
  /** Injectable fetch implementation for tests and non-browser hosts. */
  fetchImpl?: typeof fetch;
  /** Additional non-authentication headers applied to every request. */
  headers?: HeadersInit;
}

function normalizeBaseUrl(baseUrl: string): string {
  const normalized = baseUrl.trim().replace(/\/+$/, "");
  if (!normalized) {
    throw new Error("GraphqlProgrammePlatformClient: baseUrl is required");
  }
  return normalized;
}

/**
 * Rebuild a traceability map from the pairs the schema carries.
 *
 * The inverse of what `ModuleWorkspace.ploToMimlos` and `Programme.ploToMimlos`
 * do on the way out. GraphQL has no map type, so the wire carries one entry per
 * programme outcome and the contract's `Record<string, string[]>` is put back
 * here rather than in every screen that reads one.
 *
 * @param pairs - The wire's entries.
 * @returns The map, keyed by programme outcome id.
 */
function traceabilityMap(
  pairs: readonly PloTraceability[] | null | undefined,
): Record<string, string[]> {
  return Object.fromEntries((pairs ?? []).map((pair) => [pair.ploId, [...pair.mimloIds]]));
}

/**
 * Put a wire workspace back into the shape the contract declares.
 *
 * One difference, and it is structural rather than a missing field: the record
 * holds `ploToMimlos` as a map and the schema declares it as pairs. The payload
 * arrives typed as the record, so the pairs are read as the generated shape the
 * wire carries them in.
 *
 * @param workspace - The decoded wire payload.
 * @returns The workspace record.
 */
function workspaceFromWire(workspace: ModuleWorkspaceRecord): ModuleWorkspaceRecord {
  return {
    ...workspace,
    ploToMimlos: traceabilityMap(workspace.ploToMimlos as unknown as readonly PloTraceability[]),
  };
}

/**
 * Rebuild a map keyed by a closed set from the pairs the schema carries.
 *
 * The inverse of the portfolio's count pairs. `decodePayload` has already
 * turned each key back into its domain value by the time this runs, so the
 * rebuilt map is keyed the way the contract declares.
 *
 * @param pairs - The wire's entries.
 * @param key - Reads the map's key from a pair.
 * @returns The map.
 */
function countMap<P extends { count: number }>(
  pairs: readonly P[] | null | undefined,
  key: (pair: P) => string,
): Record<string, number> {
  return Object.fromEntries((pairs ?? []).map((pair) => [key(pair), pair.count]));
}

/**
 * Rebuild an open-keyed map from the key/value pairs the schema carries.
 *
 * @param pairs - The wire's entries.
 * @returns The map, or undefined when the wire carried none.
 */
function entryMap(
  pairs: readonly ExportMetadataEntry[] | null | undefined,
): Record<string, string> | undefined {
  return pairs ? Object.fromEntries(pairs.map((pair) => [pair.key, pair.value])) : undefined;
}

/**
 * Put a wire portfolio back into the shape the contract declares.
 *
 * @param summary - The decoded wire payload.
 * @returns The summary, with its counts as maps again.
 */
function portfolioFromWire(summary: PortfolioSummary): PortfolioSummary {
  return {
    ...summary,
    workflowCounts: countMap(
      summary.workflowCounts as unknown as readonly WorkflowStateCount[],
      (pair) => pair.state,
    ) as PortfolioSummary["workflowCounts"],
    deliveryCounts: countMap(
      summary.deliveryCounts as unknown as readonly DeliveryStatusCount[],
      (pair) => pair.status,
    ) as PortfolioSummary["deliveryCounts"],
  };
}

/**
 * Put wire provider settings back into the shape the contract declares.
 *
 * @param record - The decoded wire payload.
 * @returns The record, with `exportMetadata` as a map again.
 */
function providerSettingsFromWire(record: TenantProviderSettings): TenantProviderSettings {
  const metadata = entryMap(
    record.settings?.exportMetadata as unknown as readonly ExportMetadataEntry[] | undefined,
  );
  return {
    ...record,
    settings: {
      ...record.settings,
      ...(metadata === undefined ? {} : { exportMetadata: metadata }),
    },
  };
}

/**
 * The two halves of `Programme`, and why neither is `PROGRAMME_FIELDS`.
 *
 * One schema type is reached from two domain shapes. A list row is a
 * `ProgrammeSummary`: denormalised headline columns, who leads it, when they
 * last opened it. `loadProgramme` and every write answer the `Programme`
 * record: lineage, revision, and the authored document, with none of those
 * columns, because they live inside the document.
 *
 * Selecting every leaf from either would answer keys the domain's own answer
 * does not have, and the differential control compares the two field by field.
 * So each half names its own fields, and the selections suite holds their union
 * to the type: a leaf added to `Programme` has to be assigned to one side or
 * the other rather than silently reaching neither.
 */
/**
 * A change case with the team it carried and its identity ruling.
 *
 * The generated constant names only the case's own leaves. Both of these are
 * object edges, so they have to be composed by hand, and composing them once is
 * what stops one document answering a case without the team that works it or
 * the decision that moved it.
 */
const CHANGE_CASE_SELECTION = `${CURRICULUM_CHANGE_CASE_FIELDS} carriedTeam { responsibilities { ${PROGRAMME_RESPONSIBILITY_FIELDS} } moduleOwnership { ${MODULE_OWNER_FIELDS} } } identityImpactDecision { ${IDENTITY_IMPACT_DECISION_FIELDS} changedFields { ${PROGRAMME_IDENTITY_FIELD_CHANGE_FIELDS} } }`;

const READINESS_OVERRIDE_DECISION_SELECTION = `${READINESS_OVERRIDE_DECISION_FIELDS} blockerSnapshot { ${READINESS_BLOCKER_SNAPSHOT_FIELDS} }`;
const APPROVAL_CANDIDATE_SELECTION = `${APPROVAL_CANDIDATE_FIELDS} readiness { ${READINESS_RESULT_FIELDS} blockers { ${READINESS_BLOCKER_FIELDS} target { ${READINESS_BLOCKER_TARGET_FIELDS} } } } readinessOverrideBlockers { ${READINESS_BLOCKER_SNAPSHOT_FIELDS} }`;
const APPROVAL_DECISION_SELECTION = `${APPROVAL_DECISION_FIELDS} readinessOverrideBlockers { ${READINESS_BLOCKER_SNAPSHOT_FIELDS} }`;

/**
 * A tenant's provider settings, selected whole.
 *
 * Sent by the read and by the write, which answers the saved record.
 */
const PROVIDER_SETTINGS_SELECTION = `${TENANT_PROVIDER_SETTINGS_FIELDS}
  settings {
    ${PROVIDER_SETTINGS_FIELDS}
    validationContact { ${PROVIDER_CONTACT_FIELDS} }
    exportMetadata { ${EXPORT_METADATA_ENTRY_FIELDS} }
  }`;

/**
 * The platform console's overview, selected whole.
 */
const PLATFORM_OVERVIEW_SELECTION = `${PLATFORM_ADMIN_OVERVIEW_FIELDS}
  tenants { ${PLATFORM_TENANT_SUMMARY_FIELDS} }
  configurationGaps { ${TENANT_CONFIGURATION_GAP_FIELDS} }
  recentAdministrativeEvents { ${PLATFORM_ADMINISTRATIVE_AUDIT_EVENT_FIELDS} }`;

/**
 * A tenant's portfolio metrics, selected whole.
 */
const PORTFOLIO_SELECTION = `${PORTFOLIO_SUMMARY_FIELDS}
  workflowCounts { ${WORKFLOW_STATE_COUNT_FIELDS} }
  deliveryCounts { ${DELIVERY_STATUS_COUNT_FIELDS} }
  recentAuditEvents { ${AUDIT_EVENT_FIELDS} }`;

/**
 * The governance summary, selected in one place.
 *
 * Eleven lists off one payload, each of stored records. It is written once
 * because it is long, not because more than one operation sends it.
 */
const GOVERNANCE_SUMMARY_SELECTION = `${PROGRAMME_GOVERNANCE_SUMMARY_FIELDS}
  readiness {
    ${READINESS_RESULT_FIELDS}
    blockers { ${READINESS_BLOCKER_FIELDS} target { ${READINESS_BLOCKER_TARGET_FIELDS} } }
  }
  approvalCandidates {
    ${APPROVAL_CANDIDATE_SELECTION}
  }
  governanceRevisions { ${GOVERNANCE_REVISION_FIELDS} }
  curriculumRevisions { ${CURRICULUM_REVISION_FIELDS} }
  curriculumChangeCases { ${CHANGE_CASE_SELECTION} }
  programmeRelationships { ${PROGRAMME_RELATIONSHIP_FIELDS} }
  revisionLifecycle { ${REVISION_LIFECYCLE_ENTRY_FIELDS} }
  revisionPublication { ${REVISION_PUBLICATION_ENTRY_FIELDS} }
  governanceFindings { ${GOVERNANCE_FINDING_FIELDS} }
  amendmentProposals { ${AMENDMENT_PROPOSAL_FIELDS} }
  revisionApplicability { ${REVISION_APPLICABILITY_FIELDS} }
  reviewRounds { ${REVIEW_ROUND_FIELDS} }
  blockingFindings { ${PROGRAMME_REVIEW_OUTCOME_FIELDS} }
  signOffs { ${FINAL_SIGN_OFF_FIELDS} }
  snapshots { ${SNAPSHOT_FIELDS} }
  exports { ${EXPORT_FIELDS} }
  changeCaseReadiness {
    ${CHANGE_CASE_READINESS_FIELDS}
    readiness {
      ${READINESS_RESULT_FIELDS}
      blockers { ${READINESS_BLOCKER_FIELDS} target { ${READINESS_BLOCKER_TARGET_FIELDS} } }
    }
  }`;

/**
 * Drop one field from a generated leaf selection.
 *
 * There is one use: `Module.programmeId`, on the module inside a workspace. A
 * module reached from a programme gets its id from the edge that walked to it,
 * and the workspace carries `programmeId` at its top level, so the authored
 * document underneath has never held one. Selecting it would add a key the
 * domain's workspace module does not carry.
 *
 * Throws when the field is already gone, so a rename fails at import rather
 * than quietly removing nothing and putting the field back in the document.
 *
 * @param fields - A generated leaf selection, space separated.
 * @param name - The field to leave out.
 * @returns The selection without it.
 */
function without(fields: string, name: string): string {
  const remaining = fields.split(" ").filter((field) => field !== name);
  if (remaining.length === fields.split(" ").length) {
    throw new Error(`Cannot drop ${name}: the selection does not name it`);
  }
  return remaining.join(" ");
}

/**
 * The whole module workspace, selected in one place.
 *
 * Five operations answer a `ModuleWorkspace` and every one of them is read by
 * the same screen, so they select the same thing. Writing it once means a field
 * added to the workspace reaches the editor and the four mutations that return
 * it together, rather than reaching whichever document somebody remembered.
 */
const MODULE_WORKSPACE_SELECTION = `${MODULE_WORKSPACE_FIELDS}
      versions { ${MODULE_WORKSPACE_VERSION_CONTEXT_FIELDS} }
      module {
        ${without(MODULE_FIELDS, "programmeId")}
        mimlos { ${MIMLO_FIELDS} }
        casOriginalMimlos { ${MIMLO_FIELDS} }
        casDeviationNotes { ${CAS_DEVIATION_NOTE_FIELDS} }
        assessments {
          ${MODULE_ASSESSMENT_FIELDS}
          participatingModules { ${CROSS_MODULE_ASSESSMENT_PARTICIPATION_FIELDS} }
        }
        readingList { ${READING_LIST_ITEM_FIELDS} }
      }
      incomingCrossModuleAssessments { ${INCOMING_CROSS_MODULE_ASSESSMENT_FIELDS} }
      plos { ${PLO_FIELDS} standardMappings { ${PLO_STANDARD_MAPPING_FIELDS} } }
      ploToMimlos { ${PLO_TRACEABILITY_FIELDS} }
      moduleReview { ${MODULE_REVIEW_FIELDS} }
      submissionWarnings { ${MODULE_SUBMISSION_WARNING_FIELDS} }
      latestSubmission {
        ${MODULE_SUBMISSION_FIELDS}
        warnings { ${MODULE_SUBMISSION_WARNING_FIELDS} }
      }`;

export class GraphqlProgrammePlatformClient implements ProgrammePlatformClient {
  private readonly apollo: ApolloClient;

  constructor(options: GraphqlProgrammePlatformClientOptions) {
    this.apollo = createGraphqlTransport({
      endpoint: `${normalizeBaseUrl(options.baseUrl)}/graphql`,
      getAccessToken: options.getAccessToken,
      tenantId: options.tenantId,
      // Bound: the browser's `fetch` throws "Illegal invocation" when called
      // with any receiver but the global.
      fetchImpl: options.fetchImpl ?? fetch.bind(globalThis),
      headers: options.headers ?? {},
    });
  }

  /**
   * Execute one operation and return its single root field's value.
   *
   * @param field - The root field the document selects.
   * @param query - The GraphQL document.
   * @param variables - Variable values, serialised as sent.
   * @returns The field's value, `null` included, with every GraphQL enum name
   *   in it turned back into the domain value this contract declares. A field
   *   answering `JSON` is left alone: it already carries the domain's own
   *   shape, and walking it would reshape keys no schema describes.
   */
  private async operation<T>(
    field: string,
    query: string,
    variables?: Record<string, unknown>,
  ): Promise<T> {
    const data = await runOperation(this.apollo, query, variables);
    const value = data[field] as T;
    const payloadType = payloadTypeOf(field);
    return payloadType ? decodePayload(payloadType, value) : value;
  }

  async getCurrentSession(): Promise<CurrentSession> {
    return this.operation<CurrentSession>(
      "getCurrentSession",
      `query {
        getCurrentSession {
          ${SESSION_CONTEXT_FIELDS}
          tenant { ${TENANT_FIELDS} }
          tenantContext { ${TENANT_CONTEXT_FIELDS} }
          user { ${SESSION_USER_FIELDS} }
          membership { ${TENANT_MEMBERSHIP_FIELDS} }
          subject {
            ${AUTHORIZATION_SUBJECT_FIELDS}
            responsibilities { ${PROGRAMME_RESPONSIBILITY_FIELDS} }
          }
        }
      }`,
    );
  }

  async listTenants(): Promise<Tenant[]> {
    return (
      (await this.operation<Tenant[] | null>(
        "listTenants",
        `query { listTenants { ${TENANT_FIELDS} } }`,
      )) ?? []
    );
  }

  async listTenantOnboarding(): Promise<TenantOnboardingSummary[]> {
    return (
      (await this.operation<TenantOnboardingSummary[] | null>(
        "listTenantOnboarding",
        `query { listTenantOnboarding { ${TENANT_ONBOARDING_SUMMARY_FIELDS} } }`,
      )) ?? []
    );
  }

  async createTenant(input: CreateTenantInput): Promise<TenantOnboardingSummary> {
    return this.operation<TenantOnboardingSummary>(
      "createTenant",
      `mutation ($input: CreateTenantInput!) { createTenant(input: $input) { ${TENANT_ONBOARDING_SUMMARY_FIELDS} } }`,
      {
        input: {
          name: input.name,
          slug: input.slug,
          adminEmail: input.adminEmail,
          adminAlternateEmails: input.adminAlternateEmails,
          adminDisplayName: input.adminDisplayName,
        },
      },
    );
  }

  async reissueTenantInvitation(
    input: ReissueTenantInvitationInput,
  ): Promise<TenantOnboardingSummary> {
    return this.operation<TenantOnboardingSummary>(
      "reissueTenantInvitation",
      `mutation ($input: ReissueTenantInvitationInput!) { reissueTenantInvitation(input: $input) { ${TENANT_ONBOARDING_SUMMARY_FIELDS} } }`,
      {
        input: {
          tenantId: input.tenantId,
          email: input.email,
          alternateEmails: input.alternateEmails,
          displayName: input.displayName,
        },
      },
    );
  }

  async resendTenantInvitation(input: ResendTenantInvitationInput): Promise<UserSummary> {
    return this.operation<UserSummary>(
      "resendTenantInvitation",
      `mutation ($input: ResendTenantInvitationInput!) { resendTenantInvitation(input: $input) { ${USER_FIELDS} } }`,
      { input: { tenantId: input.tenantId, userId: input.userId } },
    );
  }

  async listTenantUsers(tenantId: string): Promise<UserSummary[]> {
    return (
      (await this.operation<UserSummary[] | null>(
        "listTenantUsers",
        `query ($tenantId: ID!) { listTenantUsers(tenantId: $tenantId) { ${USER_FIELDS} } }`,
        { tenantId },
      )) ?? []
    );
  }

  async inviteTenantUser(input: InviteTenantUserInput): Promise<UserSummary> {
    return this.operation<UserSummary>(
      "inviteTenantUser",
      `mutation ($input: InviteTenantUserInput!) { inviteTenantUser(input: $input) { ${USER_FIELDS} } }`,
      {
        input: {
          tenantId: input.tenantId,
          email: input.email,
          alternateEmails: input.alternateEmails,
          displayName: input.displayName,
          accountRole: encodeEnumName("AccountRole", input.accountRole),
          tenantCapabilities: input.tenantCapabilities,
        },
      },
    );
  }

  async changeTenantUserRole(input: ChangeTenantUserRoleInput): Promise<UserSummary> {
    return this.operation<UserSummary>(
      "changeTenantUserRole",
      `mutation ($input: ChangeTenantUserRoleInput!) { changeTenantUserRole(input: $input) { ${USER_FIELDS} } }`,
      {
        input: {
          tenantId: input.tenantId,
          userId: input.userId,
          accountRole: encodeEnumName("AccountRole", input.accountRole),
        },
      },
    );
  }

  async changeTenantUserCapabilities(
    input: ChangeTenantUserCapabilitiesInput,
  ): Promise<UserSummary> {
    return this.operation<UserSummary>(
      "changeTenantUserCapabilities",
      `mutation ($input: ChangeTenantUserCapabilitiesInput!) { changeTenantUserCapabilities(input: $input) { ${USER_FIELDS} } }`,
      {
        input: {
          tenantId: input.tenantId,
          userId: input.userId,
          tenantCapabilities: input.tenantCapabilities,
        },
      },
    );
  }

  async changeTenantUserStatus(input: ChangeTenantUserStatusInput): Promise<UserSummary> {
    return this.operation<UserSummary>(
      "changeTenantUserStatus",
      `mutation ($input: ChangeTenantUserStatusInput!) { changeTenantUserStatus(input: $input) { ${USER_FIELDS} } }`,
      { input: { tenantId: input.tenantId, userId: input.userId, status: input.status } },
    );
  }

  async changeTenantUserEmail(input: ChangeTenantUserEmailInput): Promise<UserSummary> {
    return this.operation<UserSummary>(
      "changeTenantUserEmail",
      `mutation ($input: ChangeTenantUserEmailInput!) { changeTenantUserEmail(input: $input) { ${USER_FIELDS} } }`,
      { input: { tenantId: input.tenantId, userId: input.userId, email: input.email } },
    );
  }

  async getTenantProviderSettings(): Promise<TenantProviderSettings> {
    return providerSettingsFromWire(
      await this.operation<TenantProviderSettings>(
        "getTenantProviderSettings",
        `query { getTenantProviderSettings { ${PROVIDER_SETTINGS_SELECTION} } }`,
      ),
    );
  }

  async updateTenantProviderSettings(
    settings: TenantProviderSettings,
  ): Promise<TenantProviderSettings> {
    return providerSettingsFromWire(
      await this.operation<TenantProviderSettings>(
        "updateTenantProviderSettings",
        `mutation ($settings: JSON!) { updateTenantProviderSettings(settings: $settings) { ${PROVIDER_SETTINGS_SELECTION} } }`,
        { settings },
      ),
    );
  }

  async addTeachingPeriodLabel(programmeId: string, label: string): Promise<string[]> {
    return this.operation<string[]>(
      "addTeachingPeriodLabel",
      `mutation ($programmeId: ID!, $label: String!) { addTeachingPeriodLabel(programmeId: $programmeId, label: $label) }`,
      { programmeId, label },
    );
  }

  async getTenantAccessPolicy(): Promise<TenantAccessPolicy> {
    return this.operation<TenantAccessPolicy>(
      "getTenantAccessPolicy",
      `query { getTenantAccessPolicy { ${TENANT_ACCESS_POLICY_FIELDS} } }`,
    );
  }

  async updateTenantAccessPolicy(policy: TenantAccessPolicy): Promise<TenantAccessPolicy> {
    return this.operation<TenantAccessPolicy>(
      "updateTenantAccessPolicy",
      `mutation ($policy: JSON!) { updateTenantAccessPolicy(policy: $policy) { ${TENANT_ACCESS_POLICY_FIELDS} } }`,
      { policy },
    );
  }

  async getPortfolioSummary(): Promise<PortfolioSummary> {
    return portfolioFromWire(
      await this.operation<PortfolioSummary>(
        "getPortfolioSummary",
        `query { getPortfolioSummary { ${PORTFOLIO_SELECTION} } }`,
      ),
    );
  }

  async getTenantPortfolio(): Promise<PortfolioProgrammeRevision[]> {
    return this.operation<PortfolioProgrammeRevision[]>(
      "getTenantPortfolio",
      `query { getTenantPortfolio { ${PORTFOLIO_PROGRAMME_REVISION_FIELDS} publicationStatus { ${PUBLICATION_STATUS_FIELDS} } latestPublication { ${PORTFOLIO_PUBLICATION_SUMMARY_FIELDS} } } }`,
    );
  }

  async getPublicCurriculumApiAccess(): Promise<PublicCurriculumApiAccess> {
    return this.operation<PublicCurriculumApiAccess>(
      "getPublicCurriculumApiAccess",
      `query { getPublicCurriculumApiAccess { ${PUBLIC_CURRICULUM_API_ACCESS_FIELDS} } }`,
    );
  }

  async setTenantPublicCurriculumApiEntitlement(
    input: SetTenantPublicCurriculumApiEntitlementInput,
  ): Promise<TenantEntitlementDecision> {
    return this.operation<TenantEntitlementDecision>(
      "setTenantPublicCurriculumApiEntitlement",
      `mutation ($input: SetTenantPublicCurriculumApiEntitlementInput!) { setTenantPublicCurriculumApiEntitlement(input: $input) { ${TENANT_ENTITLEMENT_DECISION_FIELDS} } }`,
      { input },
    );
  }

  async previewProgrammePublication(
    input: PreviewProgrammePublicationInput,
  ): Promise<ProgrammePublicationPreview> {
    return this.operation<ProgrammePublicationPreview>(
      "previewProgrammePublication",
      `query ($input: PreviewProgrammePublicationInput!) { previewProgrammePublication(input: $input) { ${PROGRAMME_PUBLICATION_PREVIEW_FIELDS} issues { ${PUBLICATION_VALIDATION_ISSUE_FIELDS} } } }`,
      { input },
    );
  }

  async publishProgramme(input: PublishProgrammeInput): Promise<CurriculumPublication> {
    return this.operation<CurriculumPublication>(
      "publishProgramme",
      `mutation ($input: PublishProgrammeInput!) { publishProgramme(input: $input) { ${CURRICULUM_PUBLICATION_FIELDS} } }`,
      { input },
    );
  }

  async withdrawProgrammePublication(
    input: WithdrawProgrammePublicationInput,
  ): Promise<CurriculumPublication> {
    return this.operation<CurriculumPublication>(
      "withdrawProgrammePublication",
      `mutation ($input: WithdrawProgrammePublicationInput!) { withdrawProgrammePublication(input: $input) { ${CURRICULUM_PUBLICATION_FIELDS} } }`,
      { input },
    );
  }

  async getPlatformAdminOverview(): Promise<PlatformAdminOverview> {
    return this.operation<PlatformAdminOverview>(
      "getPlatformAdminOverview",
      `query { getPlatformAdminOverview { ${PLATFORM_OVERVIEW_SELECTION} } }`,
    );
  }

  async getProgrammeGovernance(programmeId: string): Promise<ProgrammeGovernanceSummary> {
    return this.operation<ProgrammeGovernanceSummary>(
      "getProgrammeGovernance",
      `query ($programmeId: ID!) { getProgrammeGovernance(programmeId: $programmeId) { ${GOVERNANCE_SUMMARY_SELECTION} } }`,
      { programmeId },
    );
  }

  async listProgrammeReviewRounds(programmeId: string): Promise<ProgrammeReviewRoundTimeline[]> {
    return (
      (await this.operation<ProgrammeReviewRoundTimeline[] | null>(
        "listProgrammeReviewRounds",
        `query ($programmeId: ID!) {
          listProgrammeReviewRounds(programmeId: $programmeId) {
            ${PROGRAMME_REVIEW_ROUND_TIMELINE_FIELDS}
            round { ${REVIEW_ROUND_FIELDS} }
            outcomes { ${PROGRAMME_REVIEW_OUTCOME_FIELDS} }
            requiredReviewerStatuses { ${PROGRAMME_REVIEW_ROUND_REVIEWER_STATUS_FIELDS} }
          }
        }`,
        { programmeId },
      )) ?? []
    );
  }

  async listAccessibleProgrammes(): Promise<ProgrammeSummary[]> {
    return (
      (await this.operation<ProgrammeSummary[] | null>(
        "listAccessibleProgrammes",
        `query { listAccessibleProgrammes { ${PROGRAMME_SUMMARY_SELECTION} } }`,
      )) ?? []
    );
  }

  async listAssignedModules(): Promise<AssignedModuleSummary[]> {
    return (
      (await this.operation<AssignedModuleSummary[] | null>(
        "listAssignedModules",
        `query { listAssignedModules { ${ASSIGNED_MODULE_FIELDS} } }`,
      )) ?? []
    );
  }

  async createProgramme(input: CreateProgrammeInput): Promise<Programme> {
    return this.operation<Programme>(
      "createProgramme",
      `mutation ($input: CreateProgrammeInput!) { createProgramme(input: $input) { ${PROGRAMME_RECORD_SELECTION} } }`,
      { input: { id: input.id, structured: input.structured, leadUserId: input.leadUserId } },
    );
  }

  async loadProgramme(programmeId: string): Promise<Programme | null> {
    return this.operation<Programme | null>(
      "loadProgramme",
      `query ($programmeId: ID!) { loadProgramme(programmeId: $programmeId) { ${PROGRAMME_RECORD_SELECTION} } }`,
      { programmeId },
    );
  }

  async getProgrammeSummary(programmeId: string): Promise<ProgrammeSummary | null> {
    return this.operation<ProgrammeSummary | null>(
      "getProgrammeSummary",
      `query ($programmeId: ID!) { getProgrammeSummary(programmeId: $programmeId) { ${PROGRAMME_SUMMARY_SELECTION} } }`,
      { programmeId },
    );
  }

  async acquireEditLease(input: AcquireEditLeaseInput): Promise<EditLease> {
    return this.operation<EditLease>(
      "acquireEditLease",
      `mutation ($input: AcquireEditLeaseInput!) { acquireEditLease(input: $input) { ${EDIT_LEASE_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          moduleId: input.moduleId,
          clientId: input.clientId,
        },
      },
    );
  }

  async renewEditLease(input: RenewEditLeaseInput): Promise<EditLease> {
    return this.operation<EditLease>(
      "renewEditLease",
      `mutation ($input: RenewEditLeaseInput!) { renewEditLease(input: $input) { ${EDIT_LEASE_FIELDS} } }`,
      { input: { leaseId: input.leaseId, clientId: input.clientId } },
    );
  }

  async releaseEditLease(input: ReleaseEditLeaseInput): Promise<ReleaseEditLeasePayload> {
    return this.operation<ReleaseEditLeasePayload>(
      "releaseEditLease",
      `mutation ($input: ReleaseEditLeaseInput!) { releaseEditLease(input: $input) { ${RELEASE_EDIT_LEASE_PAYLOAD_FIELDS} } }`,
      { input: { leaseId: input.leaseId, clientId: input.clientId } },
    );
  }

  async negotiateRealtimeConnection(
    input: NegotiateRealtimeConnectionInput,
  ): Promise<RealtimeConnection> {
    return this.operation<RealtimeConnection>(
      "negotiateRealtimeConnection",
      `mutation ($input: NegotiateRealtimeConnectionInput!) {
        negotiateRealtimeConnection(input: $input) { ${REALTIME_CONNECTION_FIELDS} }
      }`,
      { input: { programmeId: input.programmeId, clientId: input.clientId } },
    );
  }

  async appendNarrativeUpdates(input: AppendNarrativeUpdatesInput): Promise<boolean> {
    return this.operation<boolean>(
      "appendNarrativeUpdates",
      `mutation ($input: AppendNarrativeUpdatesInput!) { appendNarrativeUpdates(input: $input) }`,
      { input: { programmeId: input.programmeId, updates: input.updates } },
    );
  }

  async narrativeUpdates(
    programmeId: string,
    after?: number,
    first?: number,
  ): Promise<NarrativeUpdate[]> {
    return this.operation<NarrativeUpdate[]>(
      "narrativeUpdates",
      `query ($programmeId: ID!, $after: Float, $first: Int) {
        narrativeUpdates(programmeId: $programmeId, after: $after, first: $first) { ${NARRATIVE_UPDATE_FIELDS} }
      }`,
      { programmeId, after, first },
    );
  }

  async saveProgramme(input: SaveProgrammeInput): Promise<Programme> {
    return this.operation<Programme>(
      "saveProgramme",
      `mutation ($input: SaveProgrammeInput!) { saveProgramme(input: $input) { ${PROGRAMME_RECORD_SELECTION} } }`,
      {
        input: {
          id: input.id,
          structured: input.structured,
          expectedRevision: input.expectedRevision,
          moduleBaseHashes: input.moduleBaseHashes,
          editLeaseId: input.editLeaseId,
        },
      },
    );
  }

  async getModuleWorkspace(
    programmeId: string,
    moduleId: string,
    changeCaseId?: string,
  ): Promise<ModuleWorkspaceRecord> {
    return workspaceFromWire(
      await this.operation<ModuleWorkspaceRecord>(
        "getModuleWorkspace",
        `query ($programmeId: ID!, $moduleId: ID!, $changeCaseId: ID) { getModuleWorkspace(programmeId: $programmeId, moduleId: $moduleId, changeCaseId: $changeCaseId) { ${MODULE_WORKSPACE_SELECTION} } }`,
        { programmeId, moduleId, changeCaseId },
      ),
    );
  }

  async saveModuleWorkspace(input: SaveModuleWorkspaceInput): Promise<ModuleWorkspaceRecord> {
    return workspaceFromWire(
      await this.operation<ModuleWorkspaceRecord>(
        "saveModuleWorkspace",
        `mutation ($input: SaveModuleWorkspaceInput!) { saveModuleWorkspace(input: $input) { ${MODULE_WORKSPACE_SELECTION} } }`,
        {
          input: {
            programmeId: input.programmeId,
            moduleId: input.moduleId,
            module: input.module,
            ploToMimlos: input.ploToMimlos,
            expectedRevision: input.expectedRevision,
            expectedModuleHash: input.expectedModuleHash,
            baseModule: input.baseModule,
            basePloToMimlos: input.basePloToMimlos,
            changeCaseId: input.changeCaseId,
            editLeaseId: input.editLeaseId,
          },
        },
      ),
    );
  }

  async createModule(input: CreateModuleInput): Promise<ModuleWorkspaceRecord> {
    return workspaceFromWire(
      await this.operation<ModuleWorkspaceRecord>(
        "createModule",
        `mutation ($input: CreateModuleInput!) { createModule(input: $input) { ${MODULE_WORKSPACE_SELECTION} } }`,
        {
          input: {
            programmeId: input.programmeId,
            module: input.module,
            expectedRevision: input.expectedRevision,
          },
        },
      ),
    );
  }

  async submitModuleForReview(input: SubmitModuleForReviewInput): Promise<ModuleWorkspaceRecord> {
    return workspaceFromWire(
      await this.operation<ModuleWorkspaceRecord>(
        "submitModuleForReview",
        `mutation ($input: SubmitModuleForReviewInput!) { submitModuleForReview(input: $input) { ${MODULE_WORKSPACE_SELECTION} } }`,
        {
          input: {
            programmeId: input.programmeId,
            moduleId: input.moduleId,
            expectedRevision: input.expectedRevision,
            moduleContentHash: input.moduleContentHash,
            warningsAcknowledged: input.warningsAcknowledged,
            submissionNote: input.submissionNote,
            editLeaseId: input.editLeaseId,
          },
        },
      ),
    );
  }

  async reviewModule(input: ReviewModuleInput): Promise<ModuleWorkspaceRecord> {
    return workspaceFromWire(
      await this.operation<ModuleWorkspaceRecord>(
        "reviewModule",
        `mutation ($input: ReviewModuleInput!) { reviewModule(input: $input) { ${MODULE_WORKSPACE_SELECTION} } }`,
        {
          input: {
            programmeId: input.programmeId,
            moduleId: input.moduleId,
            decision: input.decision,
            note: input.note,
          },
        },
      ),
    );
  }

  async listProgrammeResponsibilities(
    programmeId: string,
  ): Promise<ProgrammeResponsibilityAssignment[]> {
    return (
      (await this.operation<ProgrammeResponsibilityAssignment[] | null>(
        "listProgrammeResponsibilities",
        `query ($programmeId: ID!) { listProgrammeResponsibilities(programmeId: $programmeId) { ${PROGRAMME_RESPONSIBILITY_FIELDS} } }`,
        { programmeId },
      )) ?? []
    );
  }

  async listModuleOwners(programmeId: string): Promise<ModuleOwnershipAssignment[]> {
    return (
      (await this.operation<ModuleOwnershipAssignment[] | null>(
        "listModuleOwners",
        `query ($programmeId: ID!) { listModuleOwners(programmeId: $programmeId) { ${MODULE_OWNER_FIELDS} } }`,
        { programmeId },
      )) ?? []
    );
  }

  async assignProgrammeResponsibility(
    input: AssignProgrammeResponsibilityInput,
  ): Promise<ProgrammeResponsibilityAssignment> {
    return this.operation<ProgrammeResponsibilityAssignment>(
      "assignProgrammeResponsibility",
      `mutation ($input: AssignProgrammeResponsibilityInput!) { assignProgrammeResponsibility(input: $input) { ${PROGRAMME_RESPONSIBILITY_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          userId: input.userId,
          responsibility: input.responsibility,
        },
      },
    );
  }

  async assignModuleOwner(input: AssignModuleOwnerInput): Promise<ModuleOwnershipAssignment> {
    return this.operation<ModuleOwnershipAssignment>(
      "assignModuleOwner",
      `mutation ($input: AssignModuleOwnerInput!) { assignModuleOwner(input: $input) { ${MODULE_OWNER_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          moduleId: input.moduleId,
          userId: input.userId,
        },
      },
    );
  }

  async revokeProgrammeResponsibility(
    input: RevokeProgrammeResponsibilityInput,
  ): Promise<RevokeProgrammeResponsibilityPayload> {
    return this.operation<RevokeProgrammeResponsibilityPayload>(
      "revokeProgrammeResponsibility",
      `mutation ($input: RevokeProgrammeResponsibilityInput!) { revokeProgrammeResponsibility(input: $input) { ${REVOKE_PROGRAMME_RESPONSIBILITY_PAYLOAD_FIELDS} } }`,
      { input: { programmeId: input.programmeId, assignmentId: input.assignmentId } },
    );
  }

  async revokeModuleOwner(input: RevokeModuleOwnerInput): Promise<RevokeModuleOwnerPayload> {
    return this.operation<RevokeModuleOwnerPayload>(
      "revokeModuleOwner",
      `mutation ($input: RevokeModuleOwnerInput!) { revokeModuleOwner(input: $input) { ${REVOKE_MODULE_OWNER_PAYLOAD_FIELDS} } }`,
      { input: { programmeId: input.programmeId, moduleId: input.moduleId } },
    );
  }

  async transitionWorkflowWithOverride(
    input: WorkflowTransitionInput,
  ): Promise<WorkflowTransitionResult> {
    return this.operation<WorkflowTransitionResult>(
      "transitionWorkflowWithOverride",
      `mutation ($input: WorkflowTransitionInput!) { transitionWorkflowWithOverride(input: $input) { programme { ${PROGRAMME_RECORD_SELECTION} } readinessOverrideDecision { ${READINESS_OVERRIDE_DECISION_SELECTION} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          nextState: input.nextState,
          note: input.note,
          overrideReason: input.overrideReason,
          readinessOverride: input.readinessOverride,
        },
      },
    );
  }

  async approveProgramme(input: ApproveProgrammeInput): Promise<ApproveProgrammeResult> {
    return this.operation<ApproveProgrammeResult>(
      "approveProgramme",
      `mutation ($input: ApproveProgrammeInput!) { approveProgramme(input: $input) { programme { ${PROGRAMME_RECORD_SELECTION} } decision { ${APPROVAL_DECISION_SELECTION} } approvedRevision { ${GOVERNANCE_REVISION_FIELDS} } curriculumRevision { ${CURRICULUM_REVISION_FIELDS} } applicability { ${REVISION_APPLICABILITY_FIELDS} } readinessOverrideDecision { ${READINESS_OVERRIDE_DECISION_SELECTION} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          institutionalLabel: input.institutionalLabel,
          effectiveFrom: input.effectiveFrom,
          nextRevalidationDue: input.nextRevalidationDue,
          note: input.note,
          readinessOverride: input.readinessOverride,
        },
      },
    );
  }

  async openRevalidation(input: OpenRevalidationInput): Promise<OpenRevalidationResult> {
    return this.operation<OpenRevalidationResult>(
      "openRevalidation",
      `mutation ($input: OpenRevalidationInput!) { openRevalidation(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          institutionalLabel: input.institutionalLabel,
          rationale: input.rationale,
          targetEffectiveFrom: input.targetEffectiveFrom,
        },
      },
    );
  }

  async openAmendment(input: OpenAmendmentInput): Promise<OpenAmendmentResult> {
    return this.operation<OpenAmendmentResult>(
      "openAmendment",
      `mutation ($input: OpenAmendmentInput!) { openAmendment(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          institutionalLabel: input.institutionalLabel,
          classification: input.classification,
          rationale: input.rationale,
          targetEffectiveFrom: input.targetEffectiveFrom,
        },
      },
    );
  }

  async confirmChangeCaseTeam(
    input: ConfirmChangeCaseTeamInput,
  ): Promise<ConfirmChangeCaseTeamResult> {
    return this.operation<ConfirmChangeCaseTeamResult>(
      "confirmChangeCaseTeam",
      `mutation ($input: ConfirmChangeCaseTeamInput!) { confirmChangeCaseTeam(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } removedResponsibilityCount removedModuleOwnershipCount } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          decision: input.decision,
        },
      },
    );
  }

  async discardChangeCase(input: DiscardChangeCaseInput): Promise<boolean> {
    return this.operation<boolean>(
      "discardChangeCase",
      `mutation ($input: DiscardChangeCaseInput!) { discardChangeCase(input: $input) }`,
      { input: { programmeId: input.programmeId, changeCaseId: input.changeCaseId } },
    );
  }

  async withdrawChangeCase(input: WithdrawChangeCaseInput): Promise<CurriculumChangeCase> {
    return this.operation<CurriculumChangeCase>(
      "withdrawChangeCase",
      `mutation ($input: WithdrawChangeCaseInput!) { withdrawChangeCase(input: $input) { ${CHANGE_CASE_SELECTION} } }`,
      { input: { programmeId: input.programmeId, changeCaseId: input.changeCaseId } },
    );
  }

  async loadChangeCaseWorkspace(
    programmeId: string,
    changeCaseId: string,
  ): Promise<ChangeCaseWorkspace> {
    return this.operation<ChangeCaseWorkspace>(
      "loadChangeCaseWorkspace",
      `query ($programmeId: ID!, $changeCaseId: ID!) { loadChangeCaseWorkspace(programmeId: $programmeId, changeCaseId: $changeCaseId) { changeCase { ${CHANGE_CASE_SELECTION} } programme { ${PROGRAMME_RECORD_SELECTION} } } }`,
      { programmeId, changeCaseId },
    );
  }

  async saveChangeCaseWorkspace(input: SaveChangeCaseWorkspaceInput): Promise<ChangeCaseWorkspace> {
    return this.operation<ChangeCaseWorkspace>(
      "saveChangeCaseWorkspace",
      `mutation ($input: SaveChangeCaseWorkspaceInput!) { saveChangeCaseWorkspace(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } programme { ${PROGRAMME_RECORD_SELECTION} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          structured: input.structured,
          expectedRevision: input.expectedRevision,
          moduleBaseHashes: input.moduleBaseHashes,
        },
      },
    );
  }

  async submitChangeCaseForReview(
    input: SubmitChangeCaseForReviewInput,
  ): Promise<SubmitChangeCaseForReviewResult> {
    return this.operation<SubmitChangeCaseForReviewResult>(
      "submitChangeCaseForReview",
      `mutation ($input: SubmitChangeCaseForReviewInput!) { submitChangeCaseForReview(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } approvalCandidate { ${APPROVAL_CANDIDATE_SELECTION} } reviewRound { ${REVIEW_ROUND_FIELDS} } submittedRevision { ${GOVERNANCE_REVISION_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          expectedRevision: input.expectedRevision,
        },
      },
    );
  }

  async compareChangeCaseRevision(
    programmeId: string,
    changeCaseId: string,
  ): Promise<ChangeCaseRevisionComparison> {
    return this.operation<ChangeCaseRevisionComparison>(
      "compareChangeCaseRevision",
      `query ($programmeId: ID!, $changeCaseId: ID!) { compareChangeCaseRevision(programmeId: $programmeId, changeCaseId: $changeCaseId) { ${CHANGE_CASE_REVISION_COMPARISON_FIELDS} changes { ${REVISION_FIELD_CHANGE_FIELDS} } impact { ${REVISION_IMPACT_REPORT_FIELDS} } identityChanges { ${PROGRAMME_IDENTITY_FIELD_CHANGE_FIELDS} } } }`,
      { programmeId, changeCaseId },
    );
  }

  async reviewChangeCase(input: ReviewChangeCaseInput): Promise<ReviewChangeCaseResult> {
    return this.operation<ReviewChangeCaseResult>(
      "reviewChangeCase",
      `mutation ($input: ReviewChangeCaseInput!) { reviewChangeCase(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } review { ${PROGRAMME_REVIEW_OUTCOME_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          outcome: input.outcome,
          note: input.note,
        },
      },
    );
  }

  async markChangeCaseReviewed(input: MarkChangeCaseReviewedInput): Promise<CurriculumChangeCase> {
    return this.operation<CurriculumChangeCase>(
      "markChangeCaseReviewed",
      `mutation ($input: MarkChangeCaseReviewedInput!) { markChangeCaseReviewed(input: $input) { ${CHANGE_CASE_SELECTION} } }`,
      {
        input: { programmeId: input.programmeId, changeCaseId: input.changeCaseId },
      },
    );
  }

  async decideChangeCaseIdentityImpact(
    input: DecideChangeCaseIdentityImpactInput,
  ): Promise<CurriculumChangeCase> {
    return this.operation<CurriculumChangeCase>(
      "decideChangeCaseIdentityImpact",
      `mutation ($input: DecideChangeCaseIdentityImpactInput!) { decideChangeCaseIdentityImpact(input: $input) { ${CHANGE_CASE_SELECTION} } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          outcome: input.outcome,
          rationale: input.rationale,
        },
      },
    );
  }

  async createReplacementProgramme(
    input: CreateReplacementProgrammeInput,
  ): Promise<CreateReplacementProgrammeResult> {
    return this.operation<CreateReplacementProgrammeResult>(
      "createReplacementProgramme",
      `mutation ($input: CreateReplacementProgrammeInput!) { createReplacementProgramme(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } programme { ${PROGRAMME_RECORD_SELECTION} } relationship { ${PROGRAMME_RELATIONSHIP_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          replacementProgrammeId: input.replacementProgrammeId,
          programmeCode: input.programmeCode,
        },
      },
    );
  }

  async approveChangeCase(input: ApproveChangeCaseInput): Promise<ApproveChangeCaseResult> {
    return this.operation<ApproveChangeCaseResult>(
      "approveChangeCase",
      `mutation ($input: ApproveChangeCaseInput!) { approveChangeCase(input: $input) { changeCase { ${CHANGE_CASE_SELECTION} } decision { ${APPROVAL_DECISION_SELECTION} } approvedRevision { ${GOVERNANCE_REVISION_FIELDS} } curriculumRevision { ${CURRICULUM_REVISION_FIELDS} } applicability { ${REVISION_APPLICABILITY_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          changeCaseId: input.changeCaseId,
          effectiveFrom: input.effectiveFrom,
          nextRevalidationDue: input.nextRevalidationDue,
          note: input.note,
        },
      },
    );
  }

  async createGovernanceFinding(input: CreateGovernanceFindingInput): Promise<GovernanceFinding> {
    return this.operation<GovernanceFinding>(
      "createGovernanceFinding",
      `mutation ($input: CreateGovernanceFindingInput!) { createGovernanceFinding(input: $input) { ${GOVERNANCE_FINDING_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          severity: input.severity,
          message: input.message,
          sectionPath: input.sectionPath,
        },
      },
    );
  }

  async resolveGovernanceFinding(input: ResolveGovernanceFindingInput): Promise<GovernanceFinding> {
    return this.operation<GovernanceFinding>(
      "resolveGovernanceFinding",
      `mutation ($input: ResolveGovernanceFindingInput!) { resolveGovernanceFinding(input: $input) { ${GOVERNANCE_FINDING_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          findingId: input.findingId,
          resolutionNote: input.resolutionNote,
        },
      },
    );
  }

  async createRevisionApplicability(
    input: CreateRevisionApplicabilityInput,
  ): Promise<RevisionApplicability> {
    return this.operation<RevisionApplicability>(
      "createRevisionApplicability",
      `mutation ($input: CreateRevisionApplicabilityInput!) { createRevisionApplicability(input: $input) { ${REVISION_APPLICABILITY_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          governanceRevisionId: input.governanceRevisionId,
          effectiveFrom: input.effectiveFrom,
          publicationConsent: input.publicationConsent,
        },
      },
    );
  }

  async resolveRevisionApplicability(
    input: ResolveRevisionApplicabilityInput,
  ): Promise<ApplicableRevision | null> {
    return this.operation<ApplicableRevision | null>(
      "resolveRevisionApplicability",
      `query ($input: ResolveRevisionApplicabilityInput!) { resolveRevisionApplicability(input: $input) { revision { ${GOVERNANCE_REVISION_FIELDS} } applicability { ${REVISION_APPLICABILITY_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          onDate: input.onDate,
          consentedOnly: input.consentedOnly,
        },
      },
    );
  }

  async compareGovernanceRevisions(
    input: CompareGovernanceRevisionsInput,
  ): Promise<RevisionComparison> {
    return this.operation<RevisionComparison>(
      "compareGovernanceRevisions",
      `query ($input: CompareGovernanceRevisionsInput!) { compareGovernanceRevisions(input: $input) { ${REVISION_COMPARISON_FIELDS} changes { ${REVISION_FIELD_CHANGE_FIELDS} } impact { ${REVISION_IMPACT_REPORT_FIELDS} } identityChanges { ${PROGRAMME_IDENTITY_FIELD_CHANGE_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          fromRevisionId: input.fromRevisionId,
          toRevisionId: input.toRevisionId,
        },
      },
    );
  }

  async setProgrammeDeliveryStatus(input: SetProgrammeDeliveryStatusInput): Promise<Programme> {
    return this.operation<Programme>(
      "setProgrammeDeliveryStatus",
      `mutation ($input: SetProgrammeDeliveryStatusInput!) { setProgrammeDeliveryStatus(input: $input) { ${PROGRAMME_RECORD_SELECTION} } }`,
      { input: { programmeId: input.programmeId, status: input.status } },
    );
  }

  async setProgrammeEndOfLife(input: SetProgrammeEndOfLifeInput): Promise<Programme> {
    return this.operation<Programme>(
      "setProgrammeEndOfLife",
      `mutation ($input: SetProgrammeEndOfLifeInput!) { setProgrammeEndOfLife(input: $input) { ${PROGRAMME_RECORD_SELECTION} } }`,
      { input: { programmeId: input.programmeId, endOfLife: input.endOfLife } },
    );
  }

  async recordReviewOutcome(input: ReviewOutcomeInput): Promise<ProgrammeReviewOutcome> {
    return this.operation<ProgrammeReviewOutcome>(
      "recordReviewOutcome",
      `mutation ($input: ReviewOutcomeInput!) { recordReviewOutcome(input: $input) { ${PROGRAMME_REVIEW_OUTCOME_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          outcome: input.outcome,
          note: input.note,
          overrideReason: input.overrideReason,
        },
      },
    );
  }

  async completeReviewAction(
    input: CompleteReviewActionInput,
  ): Promise<CompleteReviewActionResult> {
    return this.operation<CompleteReviewActionResult>(
      "completeReviewAction",
      `mutation ($input: CompleteReviewActionInput!) { completeReviewAction(input: $input) { programme { ${PROGRAMME_RECORD_SELECTION} } review { ${PROGRAMME_REVIEW_OUTCOME_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          outcome: input.outcome,
          note: input.note,
          overrideReason: input.overrideReason,
          nextState: input.nextState,
        },
      },
    );
  }

  async recordFinalSignOff(input: RecordFinalSignOffInput): Promise<FinalSignOff> {
    return this.operation<FinalSignOff>(
      "recordFinalSignOff",
      `mutation ($input: RecordFinalSignOffInput!) { recordFinalSignOff(input: $input) { ${FINAL_SIGN_OFF_FIELDS} } }`,
      { input: { programmeId: input.programmeId, capacity: input.capacity, note: input.note } },
    );
  }

  async recordExportPreview(input: RecordExportPreviewInput): Promise<ExportRecord> {
    return this.operation<ExportRecord>(
      "recordExportPreview",
      `mutation ($input: RecordExportPreviewInput!) { recordExportPreview(input: $input) { ${EXPORT_FIELDS} } }`,
      {
        input: {
          programmeId: input.programmeId,
          format: input.format,
          template: input.template,
          versionId: input.versionId,
        },
      },
    );
  }

  async exportFinal(input: FinalExportInput): Promise<FinalExportResult> {
    return this.operation<FinalExportResult>(
      "exportFinal",
      `mutation ($input: FinalExportInput!) { exportFinal(input: $input) { programme { ${PROGRAMME_RECORD_SELECTION} } snapshot { ${SNAPSHOT_FIELDS} } exportRecord { ${EXPORT_FIELDS} } } }`,
      {
        input: {
          programmeId: input.programmeId,
          format: input.format,
          template: input.template,
        },
      },
    );
  }

  async reviseFromSnapshot(input: ReviseFromSnapshotInput): Promise<Programme> {
    return this.operation<Programme>(
      "reviseFromSnapshot",
      `mutation ($input: ReviseFromSnapshotInput!) { reviseFromSnapshot(input: $input) { ${PROGRAMME_RECORD_SELECTION} } }`,
      { input: { snapshotId: input.snapshotId, newId: input.newId, title: input.title } },
    );
  }

  async countAuditEvents(query: AuditCountQuery = {}): Promise<number> {
    return this.operation<number>(
      "countAuditEvents",
      `query ($programmeId: ID, $actor: String, $action: String, $actions: [String!], $since: DateTime, $until: DateTime) {
        countAuditEvents(programmeId: $programmeId, actor: $actor, action: $action, actions: $actions, since: $since, until: $until)
      }`,
      {
        programmeId: query.programmeId,
        actor: query.actor,
        action: query.action,
        actions: query.actions,
        since: query.since,
        until: query.until,
      },
    );
  }

  async listAuditEvents(query: AuditQuery = {}): Promise<AuditEvent[]> {
    return (
      (await this.operation<AuditEvent[] | null>(
        "listAuditEvents",
        `query ($programmeId: ID, $actor: String, $action: String, $actions: [String!], $limit: Int, $since: DateTime, $until: DateTime) {
          listAuditEvents(programmeId: $programmeId, actor: $actor, action: $action, actions: $actions, limit: $limit, since: $since, until: $until) { ${AUDIT_EVENT_FIELDS} }
        }`,
        {
          programmeId: query.programmeId,
          actor: query.actor,
          action: query.action,
          actions: query.actions,
          limit: query.limit,
          since: query.since,
          until: query.until,
        },
      )) ?? []
    );
  }

  async getNotificationFeed(query: NotificationListQuery = {}): Promise<NotificationFeed> {
    return this.operation<NotificationFeed>(
      "getNotificationFeed",
      `query ($limit: Int, $unreadOnly: Boolean) { getNotificationFeed(limit: $limit, unreadOnly: $unreadOnly) { ${NOTIFICATION_FEED_FIELDS} items { ${NOTIFICATION_FEED_ITEM_FIELDS} } } }`,
      { limit: query.limit, unreadOnly: query.unreadOnly },
    );
  }

  async markNotificationRead(notificationId: string): Promise<MarkNotificationReadPayload> {
    return this.operation<MarkNotificationReadPayload>(
      "markNotificationRead",
      `mutation ($notificationId: ID!) { markNotificationRead(notificationId: $notificationId) { ${MARK_NOTIFICATION_READ_PAYLOAD_FIELDS} } }`,
      { notificationId },
    );
  }

  async updateMyPreferences(input: UpdateMyPreferencesInput): Promise<SessionUser> {
    return this.operation<SessionUser>(
      "updateMyPreferences",
      `mutation ($input: UpdateMyPreferencesInput!) { updateMyPreferences(input: $input) { ${SESSION_USER_FIELDS} } }`,
      { input },
    );
  }

  async markAllNotificationsRead(): Promise<MarkAllNotificationsReadPayload> {
    return this.operation<MarkAllNotificationsReadPayload>(
      "markAllNotificationsRead",
      `mutation { markAllNotificationsRead { ${MARK_ALL_NOTIFICATIONS_READ_PAYLOAD_FIELDS} } }`,
    );
  }

  async listExports(programmeId: string): Promise<ExportRecord[]> {
    return (
      (await this.operation<ExportRecord[] | null>(
        "listExports",
        `query ($programmeId: ID!) { listExports(programmeId: $programmeId) { ${EXPORT_FIELDS} } }`,
        { programmeId },
      )) ?? []
    );
  }
}
